import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { METER_YEAR_DETAILS, SAVE_METER_MONTH_DETAILS } from '@graphql/meterReadDetails.gql';
import { message } from 'antd';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { mainTemplate, adjustmentsTemplate, scadaTemplate, invoiceCostAdjustmentTemplate } from './fieldTemplates';
import { MeterMonthDetails } from './meterMonthDetails';
export type { MeterMonthDetails } from './meterMonthDetails';

export interface RateContractInfo {
  rateType: string;
  rate: number | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

export interface MeterYearDetails {
  meterId: number;
  approvalStatus: string | null;
  contractStart: string | null;
  contractEnd: string | null;
  activeRateType: string | null;
  overageEmailSent: boolean | null;
  rateContracts: RateContractInfo[];
  meterSwapRows: any[];
  meter: {
    meterId: number;
    meterNumber: string | null;
    location: string | null;
    meterType: string | null;
    energyBid: number | null;
    isAgcMeter: boolean;
  } | null;
  months: MeterMonthDetails[];
}

// ─── Row shape expected by the existing table components ─────────────────────

type Cell = {
  month_year: string;
  value: number | string | null;
  id?: null;
  idName?: string;
  readonly?: boolean;
  isApproved?: boolean;
  comment?: string | null;
  isDifferentFromPrevious?: boolean;
  rateType?: string | null;
};

type Row = {
  label: string;
  field: string;
  type: string;
  editable: boolean;
  retroInput?: boolean;
  months: Cell[];
};

// ─── monthsToRows: pivots MeterMonthDetails[] into Row[] ────────────────────

function monthsToRows(
  months: MeterMonthDetails[],
  template: typeof mainTemplate,
): Row[] {
  return template.map((t) => ({
    label: t.label,
    field: (t as any).tableField ?? t.field as string,
    type: t.type,
    editable: t.editable,
    retroInput: (t as any).retroInput ?? false,
    months: months.map((m) => {
      const isApproved = m.approvalStatus === 'Approved';
      const cell: Cell = {
        month_year: m.monthYear,
        value: (m[t.field] as any) ?? null,
        id: null,
        idName: 'meter_computed_id',
        readonly: true,
        isApproved,
      };
      if (t.field === 'prodStatMeterReadType') {
        cell.comment = m.prodStatMeterReadTypeComment ?? null;
      }
      if (t.field === 'rate') {
        cell.rateType = m.rateType ?? null;
      }
      return cell;
    }),
  }));
}

function buildIsDifferentFromPrevious(rows: Row[]): Row[] {
  return rows.map((row) => {
    if (row.field !== 'rate') return row;
    const months = row.months.map((cell, i) => {
      if (i === 0) return { ...cell, isDifferentFromPrevious: false };
      const prev = row.months[i - 1];
      return {
        ...cell,
        isDifferentFromPrevious:
          cell.value != null && prev.value != null &&
          Number(cell.value) !== Number(prev.value),
      };
    });
    return { ...row, months };
  });
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useMeterReadDetailsPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const initialYearMode = searchParams.get('contract') === 'true' ? 'Contract' : 'Calendar';
  const [yearMode, setYearMode] = useState(initialYearMode);

  const meterId = id ? Number(id) : undefined;
  const shouldUseContractView = searchParams.get('contract') === 'true';
  const startMonth = searchParams.get('startMonth') || undefined;

  const navigate = useNavigate();
  const location = useLocation();

  const [saving, setSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<{
    status: 'success' | 'error';
    message: string;
  } | null>(null);

  const { data, loading, refetch, error } = useQuery(METER_YEAR_DETAILS, {
    variables: { meterId, startMonth, contract: shouldUseContractView },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    skip: !meterId,
  });

  const yearData: MeterYearDetails | null = data?.meterYearDetails ?? null;

  const contractStartDate = yearData?.contractStart ?? null;
  const contractEndDate = yearData?.contractEnd ?? null;

  // Build the row-based table data the existing table components expect
  const tableData = useMemo(() => {
    if (!yearData) return null;
    const months = yearData.months ?? [];
    const isAgcMeter = yearData.meter?.isAgcMeter ?? false;

    const rows = buildIsDifferentFromPrevious(
      monthsToRows(months, mainTemplate).filter(
        (r) => isAgcMeter || (r.field !== 'agc_energy_kwh' && r.field !== 'agc_cost')
      )
    );
    const adjustmentsRows = monthsToRows(months, adjustmentsTemplate);
    const invoiceCostAdjustmentRows = monthsToRows(months, invoiceCostAdjustmentTemplate);
    const scadaRows = monthsToRows(months, scadaTemplate);
    const meterSwapRows: Row[] = yearData.meterSwapRows ?? [];

    return {
      meterId: yearData.meterId,
      approvalStatus: yearData.approvalStatus,
      meter: yearData.meter ? {
        meterId: yearData.meter.meterId,
        meterNumber: yearData.meter.meterNumber,
        location: yearData.meter.location,
      } : null,
      contractStart: yearData.contractStart,
      contractEnd: yearData.contractEnd,
      energyBid: yearData.meter?.energyBid ?? null,
      meterType: yearData.meter?.meterType ?? null,
      activeRateType: yearData.activeRateType ?? null,
      overageEmailSent: yearData.overageEmailSent ?? null,
      rateContracts: yearData.rateContracts ?? [],
      rows,
      adjustmentsRows,
      invoiceCostAdjustmentRows,
      scadaRows,
      meterSwapRows,
      months,
    };
  }, [yearData]);

  useEffect(() => {
    if (error) message.error(error.message || 'Failed to fetch meter data');
  }, [error]);

  // Handle mode switching between Calendar and Contract
  useEffect(() => {
    const currentContract = searchParams.get('contract') === 'true';
    const targetContract = yearMode === 'Contract';

    if (currentContract !== targetContract && contractStartDate) {
      const params = new URLSearchParams(searchParams);
      const currentYear = startMonth ? startMonth.split('-')[0] : new Date().getFullYear().toString();

      if (yearMode === 'Calendar') {
        params.set('startMonth', `${currentYear}-01`);
        params.set('contract', 'false');
      } else {
        const contractMonth = contractStartDate.substring(5, 7);
        params.set('startMonth', `${currentYear}-${contractMonth}`);
        params.set('contract', 'true');
      }

      navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: false });
    }
  }, [yearMode, contractStartDate, location.pathname, navigate, searchParams, startMonth]);

  const [saveMeterMonthDetails] = useMutation(SAVE_METER_MONTH_DETAILS);

  const onSaveMonth = useCallback(async (
    monthYear: string,
    input: Record<string, any>,
    reason?: string,
  ): Promise<boolean> => {
    if (!meterId || !monthYear || Object.keys(input).length === 0) return false;
    setSaving(true);
    setSaveFeedback(null);
    try {
      const { data, errors } = await saveMeterMonthDetails({
        variables: { meterId, monthYear, input, reason },
      });
      if (errors?.length) {
        setSaveFeedback({ status: 'error', message: errors[0].message });
        return false;
      }
      const result = data?.saveMeterMonthDetails;
      if (result?.success) {
        setSaveFeedback({ status: 'success', message: result.message || 'Saved successfully!' });
        await refetch();
        return true;
      } else {
        setSaveFeedback({ status: 'error', message: result?.message || 'Failed to save' });
        return false;
      }
    } catch (err: any) {
      const msg = err.graphQLErrors?.[0]?.message || err.message || 'Failed to save';
      setSaveFeedback({ status: 'error', message: msg });
      return false;
    } finally {
      setSaving(false);
    }
  }, [meterId, saveMeterMonthDetails, refetch]);

  useEffect(() => {
    if (saveFeedback) {
      if (saveFeedback.status === 'error') message.error(saveFeedback.message);
      else message.success(saveFeedback.message);
    }
  }, [saveFeedback]);

  const parseStartMonth = (sm: string | undefined) => {
    if (!sm) { const now = new Date(); return { year: now.getFullYear(), month: 1 }; }
    const [year, month] = sm.split('-').map(Number);
    return { year, month };
  };

  const { year: currentYear, month: currentMonth } = parseStartMonth(startMonth);
  const startMonthState = currentMonth - 1;
  const startYearState = currentYear;

  const currentDate = new Date();
  const isNextYearDisabled = currentYear >= currentDate.getFullYear();

  const navigateYear = (direction: number) => {
    const newYear = currentYear + direction;
    const newMonth = shouldUseContractView && contractStartDate
      ? Number.parseInt(contractStartDate.substring(5, 7), 10)
      : 1;
    const newStartMonth = `${newYear}-${String(newMonth).padStart(2, '0')}`;
    const params = new URLSearchParams(searchParams);
    params.set('startMonth', newStartMonth);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  const isApproved = tableData?.approvalStatus === 'Approved';

  return {
    id,
    tableData,
    loading,
    saving,
    saveFeedback,
    isApproved,
    onSaveMonth,
    clearFeedback: () => setSaveFeedback(null),
    refetch,
    yearMode,
    setYearMode,
    contractStart: contractStartDate ? new Date(contractStartDate) : undefined,
    contractEnd: contractEndDate ? new Date(contractEndDate) : undefined,
    shouldUseContractView,
    energyBid: tableData?.energyBid,
    currentYear,
    navigateYear,
    isNextYearDisabled,
    startMonth: startMonthState,
    startYear: startYearState,
    // months array for blade access
    months: tableData?.months ?? [],
  };
};
